import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Link, Plugs, PaperPlaneTilt, CalendarDots, Clock, Check, X,
  Plus, Trash, Image, Eye, Megaphone, CheckCircle, XCircle,
  ArrowRight, CaretDown, Queue, Article, Lightning, ChartBar, Sparkle
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => axios.create({ baseURL: API, withCredentials: true });

const PLATFORM_COLORS = {
  facebook: '#1877F2', instagram: '#E4405F', threads: '#000000',
  x: '#000000', tiktok: '#000000',
};
const PLATFORM_NAMES = {
  facebook: 'Facebook', instagram: 'Instagram', threads: 'Threads',
  x: 'X (Twitter)', tiktok: 'TikTok',
};

// ════════════════════════════════════════
//  SOCIAL HUB - Main View
// ════════════════════════════════════════

export function ShopSocialHub() {
  const { shopId } = useParams();
  const [tab, setTab] = useState('accounts');
  const [accounts, setAccounts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConnect, setShowConnect] = useState(false);
  const [showCompose, setShowCompose] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accs, pts, tpls, plats] = await Promise.all([
        ax().get(`/api/social-hub/${shopId}/accounts`).catch(() => ({ data: [] })),
        ax().get(`/api/social-hub/${shopId}/posts`).catch(() => ({ data: [] })),
        ax().get('/api/social-hub/templates').catch(() => ({ data: [] })),
        ax().get('/api/social-hub/platforms').catch(() => ({ data: [] })),
      ]);
      setAccounts(accs.data || []);
      setPosts(pts.data || []);
      setTemplates(tpls.data || []);
      setPlatforms(plats.data || []);
    } catch {}
    setLoading(false);
  }, [shopId]);

  useEffect(() => { load(); }, [load]);

  const tabs = [
    { key: 'accounts', label: 'Accounts', icon: Plugs },
    { key: 'compose', label: 'Create Post', icon: PaperPlaneTilt },
    { key: 'from-event', label: 'From Event', icon: Sparkle },
    { key: 'scheduled', label: 'Scheduled', icon: CalendarDots },
    { key: 'published', label: 'Published', icon: CheckCircle },
    { key: 'analytics', label: 'Analytics', icon: ChartBar },
  ];

  return (
    <div data-testid="shop-social-hub">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Social Hub</h2>
          <p className="text-[13px] text-[#555] mt-1">Manage your shop's social media from one place</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/[0.03] rounded-xl p-1 mb-6">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all ${
              tab === t.key ? 'bg-[#9D4CDD]/10 text-[#9D4CDD]' : 'text-[#555]'
            }`} data-testid={`social-tab-${t.key}`}>
            <t.icon size={14} />
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Connected Accounts */}
      {tab === 'accounts' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white text-sm font-semibold">Connected Accounts</h3>
            <button onClick={() => setShowConnect(true)}
              className="px-3 py-1.5 rounded-lg bg-[#9D4CDD] text-white text-xs font-semibold"
              data-testid="connect-account-btn">
              <Plus size={12} className="inline mr-1" /> Connect
            </button>
          </div>

          {accounts.length > 0 ? (
            <div className="space-y-2">
              {accounts.map(acc => (
                <AccountCard key={acc.id} account={acc} shopId={shopId} onRefresh={load} />
              ))}
            </div>
          ) : (
            <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-8 text-center">
              <Plugs size={32} weight="duotone" className="text-[#333] mx-auto mb-3" />
              <p className="text-[#555] text-sm">No social accounts connected</p>
              <p className="text-[#333] text-xs mt-1">Connect your Facebook, Instagram, X, or Threads to start posting</p>
            </div>
          )}

          {/* Available Platforms */}
          <div className="mt-6">
            <h3 className="text-[#888] text-xs font-semibold mb-3 uppercase tracking-wider">Available Platforms</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {platforms.map(p => {
                const connected = accounts.some(a => a.platform_code === p.code && a.connection_status === 'active');
                return (
                  <div key={p.code} className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-3 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${p.color}20` }}>
                      <span className="text-xs font-bold" style={{ color: p.color }}>{p.name[0]}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-medium truncate">{p.name}</p>
                      <p className="text-[#555] text-[9px]">{p.supports_images ? 'Text + Images' : 'Text'}</p>
                    </div>
                    {connected && <Check size={14} className="text-[#10B981]" />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Create Post */}
      {tab === 'compose' && (
        <PostComposer shopId={shopId} accounts={accounts} templates={templates} onCreated={() => { setTab('scheduled'); load(); }} />
      )}

      {/* Post From Event/Tournament */}
      {tab === 'from-event' && (
        <PostFromEntity shopId={shopId} accounts={accounts} onCreated={() => { setTab('scheduled'); load(); }} />
      )}

      {/* Scheduled */}
      {tab === 'scheduled' && (
        <PostList posts={posts.filter(p => ['draft', 'queued'].includes(p.post_status))} shopId={shopId} label="Scheduled & Drafts" onRefresh={load} />
      )}

      {/* Published */}
      {tab === 'published' && (
        <PostList posts={posts.filter(p => ['published', 'partially_published'].includes(p.post_status))} shopId={shopId} label="Published Posts" onRefresh={load} />
      )}

      {/* Analytics */}
      {tab === 'analytics' && (
        <SocialAnalytics shopId={shopId} />
      )}

      {/* Connect Account Modal */}
      <ConnectModal open={showConnect} onClose={() => setShowConnect(false)} shopId={shopId} platforms={platforms} onSuccess={() => { setShowConnect(false); load(); }} />
    </div>
  );
}

// ── Account Card ──

function AccountCard({ account, shopId, onRefresh }) {
  const color = PLATFORM_COLORS[account.platform_code] || '#666';
  const name = PLATFORM_NAMES[account.platform_code] || account.platform_code;

  const disconnect = async () => {
    try {
      await ax().delete(`/api/social-hub/${shopId}/accounts/${account.id}`);
      toast.success('Disconnected');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-4 flex items-center gap-3" data-testid={`account-${account.id}`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        <span className="text-sm font-bold" style={{ color }}>{name[0]}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-semibold truncate">{account.account_display_name}</p>
        <div className="flex items-center gap-2 text-[10px]">
          <span style={{ color }}>{name}</span>
          {account.account_username && <span className="text-[#555]">@{account.account_username}</span>}
          <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase ${
            account.connection_status === 'active' ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#EF4444]/10 text-[#EF4444]'
          }`}>{account.connection_status}</span>
        </div>
      </div>
      <button onClick={disconnect} className="text-[#555] hover:text-[#EF4444] transition-colors p-1">
        <Trash size={16} />
      </button>
    </div>
  );
}

// ── Connect Modal ──

function ConnectModal({ open, onClose, shopId, platforms, onSuccess }) {
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [connecting, setConnecting] = useState(false);

  const connect = async () => {
    if (!selectedPlatform || !displayName.trim()) return;
    setConnecting(true);
    try {
      await ax().post(`/api/social-hub/${shopId}/accounts/connect`, {
        platform_code: selectedPlatform,
        account_display_name: displayName.trim(),
        account_username: username.trim(),
      });
      toast.success('Account connected!');
      setSelectedPlatform('');
      setDisplayName('');
      setUsername('');
      onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setConnecting(false);
  };

  if (!open) return null;
  const inputCls = "w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#9D4CDD]/40 focus:outline-none placeholder:text-[#444]";

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} onClick={e => e.stopPropagation()}
          className="bg-[#0e0e16] border border-white/[0.1] rounded-xl p-5 w-full max-w-sm" data-testid="connect-modal">
          <h3 className="text-white font-bold mb-4">Connect Social Account</h3>

          <div className="space-y-3 mb-4">
            <div>
              <p className="text-[#888] text-xs mb-2">Select Platform</p>
              <div className="grid grid-cols-3 gap-2">
                {platforms.map(p => (
                  <button key={p.code} onClick={() => setSelectedPlatform(p.code)}
                    className={`p-2.5 rounded-lg border text-center transition-all ${
                      selectedPlatform === p.code ? 'border-white/[0.15] bg-white/[0.04]' : 'border-white/[0.06] hover:border-white/[0.12]'
                    }`} style={selectedPlatform === p.code ? { borderColor: `${p.color}40` } : {}}
                    data-testid={`platform-${p.code}`}>
                    <span className="text-lg font-bold" style={{ color: p.color }}>{p.name[0]}</span>
                    <p className="text-[9px] text-[#888] mt-0.5">{p.name}</p>
                  </button>
                ))}
              </div>
            </div>
            <input className={inputCls} placeholder="Account / Page name" value={displayName}
              onChange={e => setDisplayName(e.target.value)} data-testid="account-name-input" />
            <input className={inputCls} placeholder="@username (optional)" value={username}
              onChange={e => setUsername(e.target.value)} />
          </div>

          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-lg bg-white/[0.04] text-[#888] text-sm">Cancel</button>
            <button onClick={connect} disabled={connecting || !selectedPlatform || !displayName.trim()}
              className="flex-1 py-2.5 rounded-lg bg-[#9D4CDD] text-white text-sm font-semibold disabled:opacity-40"
              data-testid="connect-confirm">
              {connecting ? 'Connecting...' : 'Connect'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── Post Composer ──

function PostComposer({ shopId, accounts, templates, onCreated }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [publishMode, setPublishMode] = useState('draft');
  const [scheduledFor, setScheduledFor] = useState('');
  const [creating, setCreating] = useState(false);

  const togglePlatform = (code) => {
    setSelectedPlatforms(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  const create = async () => {
    if (!title.trim() || !body.trim()) return;
    if (publishMode === 'scheduled' && !scheduledFor) {
      toast.error('Pick a date & time to schedule');
      return;
    }
    setCreating(true);
    try {
      await ax().post(`/api/social-hub/${shopId}/posts`, {
        internal_title: title.trim(),
        canonical_body: body.trim(),
        template_type: selectedTemplate || undefined,
        publish_mode: publishMode,
        scheduled_for: publishMode === 'scheduled' ? new Date(scheduledFor).toISOString() : undefined,
        platform_codes: selectedPlatforms.length > 0 ? selectedPlatforms : undefined,
      });
      toast.success(
        publishMode === 'draft' ? 'Draft saved!' :
        publishMode === 'scheduled' ? 'Post scheduled!' : 'Post queued!'
      );
      setTitle(''); setBody(''); setSelectedTemplate(''); setSelectedPlatforms([]); setScheduledFor('');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setCreating(false);
  };

  const connectedPlatforms = [...new Set(accounts.filter(a => a.connection_status === 'active').map(a => a.platform_code))];
  const inputCls = "w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#9D4CDD]/40 focus:outline-none placeholder:text-[#444]";

  return (
    <div data-testid="post-composer">
      <h3 className="text-white text-sm font-semibold mb-4">Create Social Post</h3>

      {/* Template Selection */}
      <div className="mb-4">
        <p className="text-[#888] text-xs mb-2">Start from template (optional)</p>
        <div className="flex flex-wrap gap-1.5">
          {templates.slice(0, 6).map(t => (
            <button key={t.template_code} onClick={() => setSelectedTemplate(selectedTemplate === t.template_code ? '' : t.template_code)}
              className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium border transition-all ${
                selectedTemplate === t.template_code ? 'border-[#9D4CDD]/40 bg-[#9D4CDD]/10 text-[#9D4CDD]' : 'border-white/[0.06] text-[#888] hover:text-white'
              }`}>{t.name}</button>
          ))}
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <input className={inputCls} placeholder="Post title (internal)" value={title} onChange={e => setTitle(e.target.value)} data-testid="post-title-input" />
        <textarea className={`${inputCls} resize-none`} rows={4} placeholder="Write your post content..."
          value={body} onChange={e => setBody(e.target.value)} data-testid="post-body-input" />
      </div>

      {/* Platform Selection */}
      <div className="mb-4">
        <p className="text-[#888] text-xs mb-2">Publish to</p>
        {connectedPlatforms.length > 0 ? (
          <div className="flex gap-2">
            {connectedPlatforms.map(code => {
              const selected = selectedPlatforms.includes(code);
              const color = PLATFORM_COLORS[code] || '#666';
              return (
                <button key={code} onClick={() => togglePlatform(code)}
                  className={`px-3 py-2 rounded-lg border text-xs font-medium flex items-center gap-1.5 transition-all ${
                    selected ? 'border-white/[0.15] bg-white/[0.04]' : 'border-white/[0.06] text-[#555]'
                  }`} style={selected ? { borderColor: `${color}40`, color } : {}}
                  data-testid={`select-platform-${code}`}>
                  <span className="font-bold">{(PLATFORM_NAMES[code] || code)[0]}</span>
                  {PLATFORM_NAMES[code] || code}
                  {selected && <Check size={12} />}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-[#555] text-xs">No connected accounts. Connect accounts first.</p>
        )}
      </div>

      {/* Publish Mode */}
      <div className="flex gap-2 mb-4">
        {[
          { mode: 'draft', label: 'Save Draft', icon: Article },
          { mode: 'scheduled', label: 'Schedule', icon: CalendarDots },
          { mode: 'immediate', label: 'Publish Now', icon: PaperPlaneTilt },
        ].map(m => (
          <button key={m.mode} onClick={() => setPublishMode(m.mode)}
            className={`flex-1 py-2.5 rounded-lg border text-xs font-medium flex items-center justify-center gap-1.5 transition-all ${
              publishMode === m.mode ? 'border-[#9D4CDD]/40 bg-[#9D4CDD]/10 text-[#9D4CDD]' : 'border-white/[0.06] text-[#555]'
            }`} data-testid={`publish-mode-${m.mode}`}><m.icon size={14} /> {m.label}</button>
        ))}
      </div>

      {publishMode === 'scheduled' && (
        <div className="mb-4">
          <p className="text-[#888] text-xs mb-2">Schedule for</p>
          <input type="datetime-local" className={inputCls} value={scheduledFor}
            onChange={e => setScheduledFor(e.target.value)}
            min={new Date(Date.now() + 5 * 60 * 1000).toISOString().slice(0, 16)}
            data-testid="scheduled-for-input" />
          <p className="text-[#555] text-[10px] mt-1">Posts are processed when the scheduled time passes (tick the scheduler to run).</p>
        </div>
      )}

      <button onClick={create} disabled={creating || !title.trim() || !body.trim()}
        className="w-full py-3 rounded-xl bg-[#9D4CDD] text-white font-semibold text-sm disabled:opacity-40 active:scale-[0.98] transition-all"
        data-testid="create-post-btn">
        {creating ? 'Creating...' :
          publishMode === 'draft' ? 'Save Draft' :
          publishMode === 'scheduled' ? 'Schedule Post' : 'Create & Publish'}
      </button>
    </div>
  );
}

// ── Post List ──

function PostList({ posts, shopId, label, onRefresh }) {
  const publish = async (postId) => {
    try {
      await ax().put(`/api/social-hub/${shopId}/posts/${postId}/publish`);
      toast.success('Published!');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const remove = async (postId) => {
    try {
      await ax().delete(`/api/social-hub/${shopId}/posts/${postId}`);
      toast.success('Deleted');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const STATUS_COLORS = {
    draft: '#6B7280', queued: '#F59E0B', published: '#10B981',
    failed: '#EF4444', partially_published: '#F97316', cancelled: '#6B7280',
  };

  return (
    <div data-testid="post-list">
      <h3 className="text-white text-sm font-semibold mb-4">{label}</h3>
      {posts.length === 0 ? (
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-8 text-center">
          <Article size={28} weight="duotone" className="text-[#333] mx-auto mb-2" />
          <p className="text-[#555] text-sm">No posts</p>
        </div>
      ) : (
        <div className="space-y-2">
          {posts.map(p => (
            <div key={p.id} className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-4" data-testid={`post-card-${p.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{p.internal_title}</p>
                  <p className="text-[#888] text-xs mt-0.5 line-clamp-2">{p.canonical_body}</p>
                </div>
                <span className="text-[8px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ml-2 shrink-0"
                  style={{ backgroundColor: `${STATUS_COLORS[p.post_status] || '#666'}15`, color: STATUS_COLORS[p.post_status] || '#666' }}>
                  {p.post_status}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex gap-1">
                  {p.platforms?.map(code => (
                    <span key={code} className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.04] font-medium"
                      style={{ color: PLATFORM_COLORS[code] || '#888' }}>
                      {(PLATFORM_NAMES[code] || code).slice(0, 2)}
                    </span>
                  ))}
                </div>
                <div className="flex gap-1.5">
                  {p.post_status === 'draft' && (
                    <button onClick={() => publish(p.id)}
                      className="px-3 py-1 rounded-lg bg-[#10B981]/10 text-[#10B981] text-xs font-medium"
                      data-testid={`publish-${p.id}`}>
                      Publish
                    </button>
                  )}
                  <button onClick={() => remove(p.id)} className="text-[#555] hover:text-[#EF4444] p-1">
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Post From Entity (Event / Tournament / League) ──

function PostFromEntity({ shopId, accounts, onCreated }) {
  const [entities, setEntities] = useState({ events: [], tournaments: [], leagues: [] });
  const [selectedKind, setSelectedKind] = useState('events');
  const [selected, setSelected] = useState(null);
  const [extra, setExtra] = useState('');
  const [platforms, setPlatforms] = useState([]);
  const [publishMode, setPublishMode] = useState('draft');
  const [scheduledFor, setScheduledFor] = useState('');
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ax().get(`/api/social-hub/${shopId}/entities`)
      .then(r => setEntities(r.data || { events: [], tournaments: [], leagues: [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [shopId]);

  const connected = [...new Set(accounts.filter(a => a.connection_status === 'active').map(a => a.platform_code))];

  const togglePlatform = (code) => {
    setPlatforms(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  const create = async () => {
    if (!selected) { toast.error('Pick an event/tournament/league'); return; }
    if (publishMode === 'scheduled' && !scheduledFor) { toast.error('Pick a schedule time'); return; }
    setCreating(true);
    try {
      await ax().post(`/api/social-hub/${shopId}/posts/from-entity`, {
        entity_type: selected.entity_type,
        entity_id: selected.id,
        platform_codes: platforms.length > 0 ? platforms : undefined,
        publish_mode: publishMode,
        scheduled_for: publishMode === 'scheduled' ? new Date(scheduledFor).toISOString() : undefined,
        extra_text: extra.trim() || undefined,
      });
      toast.success('Post created from ' + selected.entity_type);
      setSelected(null); setExtra(''); setPlatforms([]); setScheduledFor('');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setCreating(false);
  };

  const list = entities[selectedKind] || [];
  const kinds = [
    { key: 'events', label: 'Events' },
    { key: 'tournaments', label: 'Tournaments' },
    { key: 'leagues', label: 'Leagues' },
  ];

  return (
    <div data-testid="post-from-entity">
      <h3 className="text-white text-sm font-semibold mb-2">Promote an Event / Tournament / League</h3>
      <p className="text-[#555] text-xs mb-4">We'll auto-draft a post using the entity's details. Edit the extra text to customize.</p>

      <div className="flex gap-1 bg-white/[0.03] rounded-lg p-1 mb-4">
        {kinds.map(k => (
          <button key={k.key} onClick={() => { setSelectedKind(k.key); setSelected(null); }}
            className={`flex-1 py-1.5 rounded text-[11px] font-medium transition-all ${
              selectedKind === k.key ? 'bg-[#9D4CDD]/10 text-[#9D4CDD]' : 'text-[#555]'
            }`} data-testid={`entity-kind-${k.key}`}>{k.label} ({entities[k.key]?.length || 0})</button>
        ))}
      </div>

      {loading ? (
        <p className="text-[#555] text-sm text-center py-6">Loading...</p>
      ) : list.length === 0 ? (
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-6 text-center">
          <Sparkle size={28} weight="duotone" className="text-[#333] mx-auto mb-2" />
          <p className="text-[#555] text-sm">No {selectedKind} found for this shop</p>
        </div>
      ) : (
        <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
          {list.map(e => (
            <button key={e.id} onClick={() => setSelected(e)}
              className={`w-full text-left p-3 rounded-lg border transition-all ${
                selected?.id === e.id ? 'border-[#9D4CDD]/40 bg-[#9D4CDD]/5' : 'border-white/[0.06] bg-[#0e0e16] hover:border-white/[0.12]'
              }`} data-testid={`entity-option-${e.id}`}>
              <p className="text-white text-sm font-medium">{e.title}</p>
              {e.date && <p className="text-[#555] text-[10px] mt-0.5">{new Date(e.date).toLocaleString()}</p>}
            </button>
          ))}
        </div>
      )}

      {selected && (
        <>
          <div className="mb-4">
            <p className="text-[#888] text-xs mb-2">Extra text (optional)</p>
            <textarea rows={3} value={extra} onChange={e => setExtra(e.target.value)}
              placeholder="Add a personal message or call-to-action..."
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-[#444] focus:outline-none focus:border-[#9D4CDD]/40 resize-none"
              data-testid="extra-text-input" />
          </div>

          <div className="mb-4">
            <p className="text-[#888] text-xs mb-2">Publish to</p>
            {connected.length === 0 ? (
              <p className="text-[#555] text-xs">Connect accounts first.</p>
            ) : (
              <div className="flex gap-2 flex-wrap">
                {connected.map(code => {
                  const on = platforms.includes(code);
                  const color = PLATFORM_COLORS[code] || '#666';
                  return (
                    <button key={code} onClick={() => togglePlatform(code)}
                      className={`px-3 py-2 rounded-lg border text-xs font-medium flex items-center gap-1.5 ${
                        on ? 'border-white/[0.15] bg-white/[0.04]' : 'border-white/[0.06] text-[#555]'
                      }`} style={on ? { borderColor: `${color}40`, color } : {}}
                      data-testid={`from-entity-platform-${code}`}>
                      <span className="font-bold">{(PLATFORM_NAMES[code] || code)[0]}</span>
                      {PLATFORM_NAMES[code] || code}
                      {on && <Check size={12} />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex gap-2 mb-4">
            {[
              { mode: 'draft', label: 'Draft', icon: Article },
              { mode: 'scheduled', label: 'Schedule', icon: CalendarDots },
              { mode: 'immediate', label: 'Publish', icon: PaperPlaneTilt },
            ].map(m => (
              <button key={m.mode} onClick={() => setPublishMode(m.mode)}
                className={`flex-1 py-2 rounded-lg border text-xs font-medium flex items-center justify-center gap-1.5 ${
                  publishMode === m.mode ? 'border-[#9D4CDD]/40 bg-[#9D4CDD]/10 text-[#9D4CDD]' : 'border-white/[0.06] text-[#555]'
                }`}><m.icon size={13} /> {m.label}</button>
            ))}
          </div>

          {publishMode === 'scheduled' && (
            <input type="datetime-local" value={scheduledFor}
              onChange={e => setScheduledFor(e.target.value)}
              min={new Date(Date.now() + 5 * 60 * 1000).toISOString().slice(0, 16)}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white text-sm mb-4"
              data-testid="from-entity-schedule-input" />
          )}

          <button onClick={create} disabled={creating}
            className="w-full py-3 rounded-xl bg-[#9D4CDD] text-white font-semibold text-sm disabled:opacity-40"
            data-testid="create-from-entity-btn">
            {creating ? 'Creating...' :
              publishMode === 'draft' ? 'Save Draft' :
              publishMode === 'scheduled' ? 'Schedule Post' : 'Publish Now'}
          </button>
        </>
      )}
    </div>
  );
}

// ── Analytics ──

function SocialAnalytics({ shopId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ax().get(`/api/social-hub/${shopId}/analytics`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [shopId]);

  if (loading) return <p className="text-[#555] text-sm text-center py-6">Loading analytics...</p>;
  if (!data) return <p className="text-[#555] text-sm text-center py-6">No data</p>;

  const { totals, per_platform, note } = data;

  return (
    <div data-testid="social-analytics">
      <h3 className="text-white text-sm font-semibold mb-4">Social Performance</h3>

      <div className="grid grid-cols-2 gap-2 mb-5">
        {[
          { label: 'Total Posts', value: totals.posts, color: '#9D4CDD' },
          { label: 'Published', value: totals.published, color: '#10B981' },
          { label: 'Scheduled', value: totals.scheduled, color: '#F59E0B' },
          { label: 'Drafts', value: totals.drafts, color: '#6B7280' },
        ].map(s => (
          <div key={s.label} className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-4" data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>
            <p className="text-[#888] text-[10px] uppercase tracking-wider font-bold">{s.label}</p>
            <p className="text-2xl font-bold mt-1" style={{ color: s.color, fontFamily: 'Unbounded' }}>{s.value}</p>
          </div>
        ))}
      </div>

      {Object.keys(per_platform).length > 0 ? (
        <>
          <p className="text-[#888] text-xs uppercase tracking-wider font-bold mb-3">By Platform</p>
          <div className="space-y-2 mb-4">
            {Object.entries(per_platform).map(([code, p]) => (
              <div key={code} className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-4" data-testid={`platform-stat-${code}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${PLATFORM_COLORS[code] || '#666'}20` }}>
                      <span className="text-xs font-bold" style={{ color: PLATFORM_COLORS[code] || '#666' }}>{p.platform[0]}</span>
                    </div>
                    <p className="text-white text-sm font-semibold">{p.platform}</p>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] font-bold">{p.published} posts</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div><p className="text-[#555] text-[9px] uppercase">Impressions</p><p className="text-white text-sm font-bold">{p.impressions.toLocaleString()}</p></div>
                  <div><p className="text-[#555] text-[9px] uppercase">Clicks</p><p className="text-white text-sm font-bold">{p.clicks.toLocaleString()}</p></div>
                  <div><p className="text-[#555] text-[9px] uppercase">Engage Rate</p><p className="text-white text-sm font-bold">{p.engagement_rate}%</p></div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-6 text-center">
          <ChartBar size={28} weight="duotone" className="text-[#333] mx-auto mb-2" />
          <p className="text-[#555] text-sm">Publish posts to see platform analytics</p>
        </div>
      )}

      {note && (
        <div className="bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-lg p-3">
          <p className="text-[#F59E0B] text-[11px]">ⓘ {note}</p>
        </div>
      )}
    </div>
  );
}
